/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Workspace management", description = "Hightouch provides a full suite of platform governance features to ensure security and compliance through access and change management.", lastUpdatedDate = new Date(1727654400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Workspace management",
        description: "Hightouch provides a full suite of platform governance features to ensure security and compliance through access and change management.",
        lastUpdatedDate: "Mon Sep 30 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Understanding how your organization is structured",
          "depth": 0,
          "id": "understanding-how-your-organization-is-structured"
        }, {
          "value": "Organizations",
          "depth": 1,
          "id": "organizations"
        }, {
          "value": "Workspaces",
          "depth": 1,
          "id": "workspaces"
        }, {
          "value": "User groups",
          "depth": 1,
          "id": "user-groups"
        }, {
          "value": "Roles",
          "depth": 1,
          "id": "roles"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      img: "img",
      h3: "h3",
      strong: "strong",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Hightouch makes it easy for teams to collaborate across your business. Workspace management is designed to be simple so that you can get started quickly. At the same time, it offers deep flexibility to support large enterprises with multiple teams, regional divisions, and complex brand structures."
      }), "\n", _jsxs(_components.p, {
        children: ["This guide explains the core concepts behind workspace management, starting with ", _jsx(_components.a, {
          href: "/workspace-management/user-management",
          children: "user groups"
        }), " and ", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: "role-based access control"
        }), ". After setting up your workspace, you can activate additional features like ", _jsx(_components.a, {
          href: "/workspace-management/approval-flows",
          children: "approval flows"
        }), " and ", _jsx(_components.a, {
          href: "/workspace-management/environments",
          children: "staging environments"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "understanding-how-your-organization-is-structured",
        children: "Understanding how your organization is structured"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/overview-understanding-the-organizational-structure.jpg",
          alt: "Organizational structure"
        })
      }), "\n", _jsx(_components.h3, {
        id: "organizations",
        children: "Organizations"
      }), "\n", _jsxs(_components.p, {
        children: ["An ", _jsx(_components.strong, {
          children: "organization"
        }), " in Hightouch represents your entire company. It's the top-level entity used for billing and user management."]
      }), "\n", _jsx(_components.h3, {
        id: "workspaces",
        children: "Workspaces"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "workspace"
        }), " is a partition within an organization, typically used to isolate environments (like staging and production) or to divide business units that don't share any resources in common. Each workspace is a completely separate container that houses its own set of resources like syncs and models. By design, nothing can be shared across workspace boundaries—not even the connection to your data source."]
      }), "\n", _jsx(_components.p, {
        children: "While workspaces provide flexibility in structuring your organization, we recommend keeping the number of workspaces to a minimum. Each workspace requires its own configuration, so consolidating into fewer workspaces can help streamline both your onboarding and day-to-day operations."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch's governance features allow different teams, regions, or brands to operate within a single workspace that shares common settings. Strict access controls ensure that only authorized users can interact with specific resources. For most enterprise customers, a single production workspace is sufficient, though some choose to add an additional workspace for testing purposes. Features like ", _jsx(_components.a, {
          href: "/workspace-management/folders-filters",
          children: "labels, filters, and folders"
        }), " help keep everything organized."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Separate workspaces should only be considered if regions or brands must remain\nfully independent, or if compliance requires data processing in different\nlocations."
        })
      }), "\n", _jsx(_components.h3, {
        id: "user-groups",
        children: "User groups"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "user group"
        }), " in Hightouch is a collection of users who share the same permissions. Access control is managed at the group level, rather than for individual users, to ensure consistency and make updates easier as team structures and responsibilities evolve."]
      }), "\n", _jsx(_components.p, {
        children: "User groups are created within organizations and can be granted access to one or more workspaces. Users can belong to multiple groups, and when they do, they inherit the combined access from all of their assigned groups."
      }), "\n", _jsx(_components.p, {
        children: "Typically, user-to-group mappings are determined by your identity provider (e.g., Okta). However, if SSO isn't enabled, or if you need to create custom groups, you can manually configure user groups and assign users directly in the Hightouch app."
      }), "\n", _jsx(_components.h3, {
        id: "roles",
        children: "Roles"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "role"
        }), " defines the specific actions a user group can perform within a workspace. The key distinction between user groups and roles is that a user group aggregates users, while a role defines the scope of what actions those users can take. For example, a user group called “Lifecycle marketers” may assume the role of “Viewer” in a workspace."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch offers two types of roles:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Pre-defined roles"
          }), ": These are pre-defined roles like “Admin,” “Editor,” and “Viewer” that apply broadly to all resources in a workspace. These roles are designed to simplify onboarding but in some cases may not be flexible enough for large, busy workspaces."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom roles"
          }), ": These provide granular control, allowing you to grant specific abilities like creating syncs, accessing sensitive data, or managing credentials. Custom roles can be fine-tuned for individual sources and destinations."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
